import { Button } from '@accure/ui';
import { CheckIcon } from '@heroicons/react/24/outline';
import { FileText } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';
import { z } from 'zod';

import { TableCell, TableRow } from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { codec, useQueryParams } from '@/hooks/router/use-query-params';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import type { Document } from '@/modules/documents/domain/entities/document';
import { formatDate } from '@/utils/format/date';
import { formatListInline } from '@/utils/format/list';

import { DocumentAction } from '../../components/pages/site-documents/document-action';

import { DocumentSummaryDialog } from './document-summary-dialog';
import type { DocumentsTableProps } from './documents-table';

export type DocumentsTableRowProps = {
  document: Document;
  siteId?: string;
} & Pick<DocumentsTableProps, 'onEditClick'>;

export function DocumentsTableRow({
  document,
  onEditClick,
  siteId,
}: DocumentsTableRowProps) {
  const featureFlag = useFeatureFlag();
  const showSummmaryDialog = useBoolean();
  const [, queryParams] = useQueryParams({
    id: {
      key: 'id',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });

  function previewOrDownloadDocument() {
    queryParams.push({ id: document.id });
  }

  return (
    <>
      <TableRow
        id={document.id}
        className="cursor-pointer"
        onClick={previewOrDownloadDocument}
      >
        <TableCell>
          {document.required ? (
            <Tooltip delay content="Required document">
              <CheckIcon className="m-1 size-4 text-green-500" />
            </Tooltip>
          ) : null}
        </TableCell>
        <TableCell>{document.name}</TableCell>
        <TableCell>{formatDate(document.updatedAt)}</TableCell>
        <TableCell>{document.uploadedBy.displayName}</TableCell>
        <TableCell>{document.type}</TableCell>
        <TableCell>{formatListInline(document.tags, 'conjunction')}</TableCell>
        <TableCell className="flex justify-center">
          <div
            onClick={(e) => e.stopPropagation()}
            className="items-center space-x-2"
          >
            <DocumentAction
              document={document}
              siteId={siteId}
              onEditClick={() => onEditClick(document)}
              onPreviewClick={previewOrDownloadDocument}
            />
            {featureFlag('ai') ? (
              <Tooltip delay content="View AI summary" position="left">
                <Button
                  data-testid="document-summary-button"
                  variant="secondary"
                  size="icon"
                  className="rounded-full bg-white/10 text-primary-500 hover:text-primary-600"
                  onClick={() => showSummmaryDialog.setTrue()}
                >
                  <FileText />
                </Button>
              </Tooltip>
            ) : null}
          </div>
          {showSummmaryDialog.value && featureFlag('ai') ? (
            <DocumentSummaryDialog
              size="3xl"
              open={showSummmaryDialog.value}
              onClose={showSummmaryDialog.setFalse}
              document={document}
            />
          ) : null}
        </TableCell>
      </TableRow>
    </>
  );
}
